import '@/styles/common/Preview/preview.scss';

import { AxiosError, AxiosResponse } from 'axios';
import cn from 'classnames';
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';

import BaseButton from '@/components/common/Button/BaseButton';
import { NotificationMatchbou } from '@/components/common/Notification/Matchbou';
import { AdminPage } from '@/componentsAdmin/Layout/Page';
import HeaderTitle from '@/components/common/Title/HeaderTitle';
import { ModalDefault } from '@/componentsAdmin/Parts/Modal';
import { ResumePreviewPage1 } from '@/components/Resume/Preview/Page1';
import { ResumePreviewPage2 } from '@/components/Resume/Preview/Page2';
import { PreviewPage } from '@/components/CareerHistory/';
import {
  FormContainerDropdown,
  FormContainerDropdownMultiple,
  FormContainerTextArea,
  FormLabel,
  FormLayoutFieldset,
} from '@/componentsAdmin/Parts/Form';
import { notificationSuccess, notificationError, toggleLoading } from '@/reduxAdmin/modules/utils';
import { careerHistory } from '@/hooksAdmin/usePageCareerHistory';
import { MESSAGE } from '@/definitionAdmin/MESSAGE';
import { DIRECT_FORM_OPTIONS } from '@/definitionDirect/FORM_OPTIONS';
import {
  CareerSummaryModel,
  EducationData,
  MBResumeResponse,
  Qualification,
  ADMINApi,
  UserInfo,
  ResponseError,
} from '@/utils/api-client/api';

export type TFetchDataProps = {
  fetchData: MBResumeResponse;
};

type TInputAgencyProps = {
  status?: number;
  comment?: string;
};

type TInputAgencyInputProps = {
  status?: string;
  comment_2?: string;
  comment_3?: string;
  comment_select?: string[];
};

type THistoryData = {
  educations1: EducationData[];
  educations2: EducationData[];
  careers1: CareerSummaryModel[];
  careers2: CareerSummaryModel[];
  qualifications: Qualification[];
};

/**
 * # 履歴書/職務経歴書 プレビュー /admin/transcription/{UserId}
 */
export const AdminPagesTranscriptionPreview = (): React.ReactElement => {
  // ====================================================================================================
  //
  // HOOKS
  //
  // ====================================================================================================
  const dispatch = useDispatch();
  const params = useParams<{ matchbox_id: string }>();
  const matchboxId = useMemo(() => params.matchbox_id, [params]);
  const methods = useForm<TInputAgencyInputProps>();
  const history = useHistory();

  // ====================================================================================================
  //
  // useState
  //
  // ====================================================================================================
  const [modal, setModal] = useState<boolean>(false);
  const [sendDisabled, setDisabled] = useState<boolean>(true);
  const [sendDisabledCompleted, setDisabledCompleted] = useState<boolean>(false);
  const [sendConfirm, setSendConfirm] = useState<boolean>(false);
  const [sendCompleted, setSendCompleted] = useState<boolean>(false);
  const [notification, setNotification] = useState<boolean>(true);
  const [resumeGetData, setResumeGetData] = useState<MBResumeResponse | undefined>();

  const checkInputAgencyStatus = async () => {
    if (!matchboxId) return;
    const res = await new ADMINApi().getAdminInputAgency(matchboxId);
    if (res.data && res.data.status !== 1) {
      history.push(`/admin/users/${matchboxId}`);
    }
  };

  const checkInput = async () => {
    if (!matchboxId) return;
    const user: AxiosResponse = await new ADMINApi().getAdminUserId(matchboxId);
    if (!user) return;
    const resume = await new ADMINApi().getAdminInputAgencyResume(user.data.user_info.user_id);
    setResumeGetData(resume.data);
    const ch = await new ADMINApi().getAdminInputAgencyCareerHistory(user.data.user_info.user_id);
    if (!resume.data.user?.last_update_resume && !ch.data.latest_update) {
      setDisabledCompleted(true);
    }
  };

  const changeAction = (value: TInputAgencyInputProps, name: string) => {
    if (value.status === '2') {
      changeReason(value);
    } else if (value.status === '3') {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  };

  const changeReason = (value: TInputAgencyInputProps) => {
    if (value.comment_select?.length) {
      setDisabled(false);
    } else {
      if (methods.getValues('comment_2')) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    }
  };

  const sendInputAgency = async () => {
    if (!params.matchbox_id) return;
    dispatch(toggleLoading(true));
    const val = methods.getValues();
    const patch_params: TInputAgencyProps = { status: Number(val.status) };
    if (patch_params.status === 3 && val.comment_3) {
      patch_params.comment = val.comment_3;
    }
    if (patch_params.status === 2) {
      if (val.comment_select?.length) {
        patch_params.comment = '';
        val.comment_select?.forEach((text: string) => {
          patch_params.comment = patch_params.comment + text + '\n';
        });
      }
      if (val.comment_2) {
        if (patch_params.comment) {
          patch_params.comment = patch_params.comment + val.comment_2;
        } else {
          patch_params.comment = val.comment_2;
        }
      }
    }

    try {
      await new ADMINApi().patchAdminInputAgency(params.matchbox_id, patch_params);
      dispatch(notificationSuccess(MESSAGE.patch_input_agency));
      // setSendConfirm(false);
      // setModal(false);
      // history.push(`/admin/users/${params.matchbox_id}`);
      setSendCompleted(true);
    } catch (error) {
      const e = error as AxiosError<ResponseError>;
      if (e.response) dispatch(notificationError(e.response.data.error_message));
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  const clickModalClose = () => {
    if (!sendCompleted) {
      setModal(false);
    } else {
      history.push(`/admin/users/${params.matchbox_id}`);
    }
  };

  useEffect(() => {
    methods.watch((value: TInputAgencyInputProps, { name }) => {
      changeAction(value, name as string);
    });
  }, [methods.watch]);

  useEffect(() => {
    checkInputAgencyStatus();
    checkInput();
  }, [matchboxId]);

  return (
    <AdminPage
      type="preview"
      public_header
      header={
        <div className="sticky top-0 z-10">
          <HeaderTitle title={`${params.matchbox_id}｜入力代行【履歴書・職務経歴書】`}>
            <div className="flex justify-end space-x-24">
              <BaseButton
                className="mbx-top-preview-edit-button"
                href={`/admin/transcription/${params.matchbox_id}/resume/edit`}
                theme="secondary"
                size="s"
              >
                履歴書を編集する
              </BaseButton>
              <BaseButton
                className="mbx-top-preview-edit-button"
                href={`/admin/transcription/${params.matchbox_id}/cv/edit`}
                theme="secondary"
                size="s"
              >
                職務経歴書を編集する
              </BaseButton>
              <BaseButton
                className="mbx-top-preview-edit-button"
                theme="primary"
                size="s"
                onClick={() => setModal(true)}
              >
                反映を確定する
              </BaseButton>
            </div>
          </HeaderTitle>
        </div>
      }
    >
      <>
        <div className="sticky top-60 z-10 mt-16 pb-16">
          <NotificationMatchbou
            show={notification}
            showMatchbou={false}
            onClick={() => setNotification(false)}
          >
            職務経歴書に入力代行した内容はMATCHBOX登録企業にそのまま公開される可能性があるため、個人情報を入力しないようにしてください。
          </NotificationMatchbou>
        </div>
        <PageMainContent resumeGetData={resumeGetData} />
        <div className="sm:mb-170"></div>
        <ModalDefault
          isOpen={modal}
          onClose={() => clickModalClose()}
          className="rounded-modalContainer"
        >
          {!sendCompleted ? (
            <div className="w-624 p-80">
              {!sendConfirm ? (
                <>
                  <p className="mbx-typography--heading_3 text-center">
                    求職者へ通知メールを送信する
                  </p>
                  <FormProvider {...methods}>
                    <form id="inputAgencyFORM" className="mt-32 text-left">
                      <FormLayoutFieldset className="w-300">
                        <p className="mbx-typography--caption_1 text-left">
                          <span>入力代行のアクションを選択してください。</span>
                        </p>
                        <FormContainerDropdown
                          className="mt-8"
                          name="status"
                          placeholder="選択してください"
                          selectList={DIRECT_FORM_OPTIONS.inputAgencyAction}
                        />
                      </FormLayoutFieldset>
                      {methods.watch('status') === '3' && (
                        <FormLayoutFieldset className="mt-32">
                          <p className="mbx-typography--caption_1 text-left">
                            <span>必要に応じて求職者へメッセージを送りましょう。</span>
                          </p>
                          <FormContainerTextArea
                            className="mt-8"
                            name="comment_3"
                            placeholder="例）履歴書の自己PRについては一部文字数制限内に収めるために文章を短縮させていただきました。"
                            maxLength={500}
                          />
                        </FormLayoutFieldset>
                      )}
                      {methods.watch('status') === '2' && (
                        <div>
                          <FormLayoutFieldset className="mt-32">
                            <p className="mbx-typography--caption_1 text-left">
                              <span>見送り理由を選択してください。</span>
                            </p>
                            <FormContainerDropdownMultiple
                              className="mt-8"
                              name="comment_select"
                              placeholder="選択してください"
                              selectList={DIRECT_FORM_OPTIONS.inputAgencyReject}
                            />
                          </FormLayoutFieldset>
                          <FormLayoutFieldset className="mt-32">
                            <p className="mbx-typography--caption_1 text-left">
                              <span>
                                上記以外の選択肢、または補足がある場合は入力してください。
                              </span>
                            </p>
                            <FormContainerTextArea
                              className="mt-8"
                              name="comment_2"
                              placeholder="例）書類のパスワード設定を削除した状態で再度ご提出ください。"
                              maxLength={500}
                            />
                          </FormLayoutFieldset>
                        </div>
                      )}
                    </form>
                  </FormProvider>
                  <div className="flex gap-x-16 justify-center mt-24">
                    <div className="w-224">
                      <BaseButton
                        theme="secondary"
                        size="m"
                        className="w-full"
                        onClick={() => setModal(false)}
                      >
                        キャンセル
                      </BaseButton>
                    </div>
                    <div
                      className={cn(
                        'w-224',
                        (sendDisabled ||
                          (methods.watch('status') === '3' && sendDisabledCompleted)) &&
                          'pointer-events-none'
                      )}
                    >
                      <BaseButton
                        theme="primary"
                        size="m"
                        className="w-full"
                        disabled={
                          sendDisabled || (methods.watch('status') === '3' && sendDisabledCompleted)
                        }
                        onClick={() => setSendConfirm(true)}
                      >
                        この内容で送信する
                      </BaseButton>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {methods.watch('status') === '3' && (
                    <>
                      <p className="mbx-typography--heading_3 text-center">入力代行が完了</p>
                      <p className="mbx-typography--body_1 mt-24">
                        この求職者に入力代行の完了の通知メールを送信しますか？
                        <br />
                        ※求職者に入力代行の完了通知メールを送ると、編集を行うことができなくなります。
                      </p>
                    </>
                  )}
                  {methods.watch('status') === '2' && (
                    <>
                      <p className="mbx-typography--heading_3 text-center">入力代行を見送る</p>
                      <p className="mbx-typography--body_1 mt-24">
                        この求職者の入力代行を見送りますか？
                        <br />
                        ※求職者に入力代行の完了通知メールを送ると、編集を行うことができなくなります。
                      </p>
                    </>
                  )}
                  <div className="flex gap-x-16 justify-center mt-32">
                    <div className="w-160">
                      <BaseButton
                        theme="secondary"
                        size="m"
                        className="w-full"
                        onClick={() => setSendConfirm(false)}
                      >
                        キャンセル
                      </BaseButton>
                    </div>
                    <div className="w-160">
                      <BaseButton
                        theme="primary"
                        size="m"
                        className="w-full"
                        onClick={() => sendInputAgency()}
                      >
                        この内容で送信する
                      </BaseButton>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="w-624 py-80 px-48">
              <p className="mbx-typography--heading_3 text-center">
                求職者に通知メールを送信しました
              </p>
              <div className="w-200 mx-auto mt-40">
                <BaseButton
                  theme="primary"
                  size="m"
                  className="w-full"
                  href={`/admin/users/${params.matchbox_id}`}
                >
                  求職者詳細へ戻る
                </BaseButton>
              </div>
            </div>
          )}
        </ModalDefault>
      </>
    </AdminPage>
  );
};

const PageMainContent = ({
  resumeGetData,
}: {
  resumeGetData?: MBResumeResponse;
}): React.ReactElement => {
  const historyData: THistoryData = {
    educations1: [],
    educations2: [],
    careers1: [],
    careers2: [],
    qualifications: [],
  };

  /**
   * 職歴の入社と退社を分ける
   */
  const separateJoinAndLeave = (data: CareerSummaryModel[], careerLength: number) => {
    if (data.length === 0) return [];
    const careers = [];
    const reverseFlag =
      data.length === 1
        ? true
        : data[0].c_join_date < data[data.length - 1].c_join_date
        ? true
        : false;
    if (reverseFlag) data.reverse();
    const leaveCount = data.filter((career) => career.c_leave_flag).length;
    for (let i = 0; careers.length < Math.min(careerLength, data.length + leaveCount); i++) {
      careers.push(data[i]);
      if (careers.length < careerLength && data[i].c_leave_flag) {
        careers.push(Object.assign({ isJoin: true }, careersData[i]));
      }
    }
    if (reverseFlag) careers.reverse();
    return careers;
  };

  const resumeData = resumeGetData;

  const educationsData = resumeData?.educations ? Array.from(resumeData?.educations.values()) : [];
  const careersData = resumeData?.careers ? Array.from(resumeData?.careers.values()) : [];
  const qualificationsData = resumeData?.qualifications
    ? Array.from(resumeData?.qualifications.values())
    : [];
  // // テスト用にdummyデータを使う
  // const educationsData = dummyEducationsData;
  // const careersData = dummyCareersData;
  // const qualificationsData = dummyQualificationsData;

  if (educationsData.length >= 25) {
    // 学歴が25件以上の場合
    const educations = educationsData.slice(-25);
    historyData.educations1 = educations.splice(0, 16);
    historyData.educations2 = educations;
  } else if (educationsData.length >= 16) {
    // 学歴が16件以上の場合(2ページ目にまたぐ)
    historyData.educations1 = educationsData.splice(0, 16);
    historyData.educations2 = educationsData;
    const careerLength = 9 - historyData.educations2.length;
    const careers = separateJoinAndLeave(careersData, careerLength);
    historyData.careers2 = careers;
  } else {
    // 学歴が16件未満の場合(1ページ目に収まる)
    historyData.educations1 = educationsData;
    const careerLength = 25 - historyData.educations1.length;
    const careers = separateJoinAndLeave(careersData, careerLength);
    historyData.careers1 = careers.splice(0, 16 - historyData.educations1.length);
    historyData.careers2 = careers;
  }
  if (
    historyData.educations2.length + historyData.careers2.length === 0 &&
    historyData.educations1.length < 16
  ) {
    // 1ページ目に学歴、職歴がおさまる場合
    historyData.qualifications = qualificationsData.splice(-10);
  } else if (historyData.educations2.length + historyData.careers2.length < 9) {
    // 2ページ目にまたぐが資格・免許を表示する余地がある場合
    historyData.qualifications = qualificationsData.splice(
      historyData.educations2.length + historyData.careers2.length - 9
    );
  }

  const lastUpdateDate = resumeData?.user?.last_update_resume
    ? resumeData?.user?.last_update_resume.split(' ')[0]
    : resumeData?.user?.registration_date;
  const date = lastUpdateDate
    ? `${lastUpdateDate.split('-')[0]}年${lastUpdateDate.split('-')[1]}月${
        lastUpdateDate.split('-')[2]
      }日`
    : '';

  return (
    <div className="mbx-preview mbx-utils-stack-v--preview w-840 mx-auto" id="PREVIEW_RESUME">
      <ResumePreviewPage1
        date={date}
        fetchData={resumeData}
        educations={historyData.educations1}
        careers={historyData.careers1}
        secondPageCareersItemLength={historyData.careers2.length}
      />
      <ResumePreviewPage2
        date={date}
        fetchData={resumeData}
        educations={historyData.educations2}
        careers={historyData.careers2}
        qualifications={historyData.qualifications}
        firstPageEducationsItemLength={historyData.educations1.length}
      />
      <PageMainContentCv />
      <div className="pb-40"></div>
    </div>
  );
};

const PageMainContentCv = () => {
  const { data: fetchData, user } = careerHistory(true);
  const [scale, setScale] = useState<number>(1);
  const [height, setHeight] = useState<number>(1188);

  useEffect(() => {
    const mbxPreviewPage = document.getElementsByClassName('mbx-preview-page')[0];
    if (!mbxPreviewPage) return;
    const mbxPreviewPageWidth = mbxPreviewPage.getBoundingClientRect().width;
    const mbxPreviewContent = document.getElementsByClassName('mbx-preview')[0];
    const aspectRatio = mbxPreviewPage.getBoundingClientRect().height / mbxPreviewPageWidth;
    const resizeObserver = new ResizeObserver(() => {
      const mbxPreviewContentWidth = mbxPreviewContent.getBoundingClientRect().width;
      if (window.innerWidth < 1024) {
        setScale(mbxPreviewContentWidth / mbxPreviewPageWidth);
        setHeight(mbxPreviewContentWidth * aspectRatio);
      } else {
        setScale(1);
        setHeight(mbxPreviewPageWidth * aspectRatio);
      }
    });
    resizeObserver.observe(window.document.body);
    return () => resizeObserver.unobserve(window.document.body);
  }, []);

  const date = useMemo<string>(() => {
    if (!fetchData?.latest_update) return '';
    const dateAry = fetchData?.latest_update.split('-');
    return `${dateAry[0]}年${dateAry[1]}月${dateAry[2]}日`;
  }, [fetchData]);

  return (
    <div id="inputAgencyPreview">
      <PreviewPage
        scale={scale}
        // height={height}
        date={date}
        fetchData={fetchData}
        user={user}
        type={'admin_input_agency'}
      />
    </div>
  );
};
